import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Stack,
  Divider,
  Chip,
  Alert,
  Grid,
  InputAdornment,
  alpha,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import html2pdf from 'html2pdf.js';

// Icons
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import PreviewIcon from "@mui/icons-material/Preview";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DraftsIcon from "@mui/icons-material/Drafts";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import InventoryIcon from "@mui/icons-material/Inventory";
import PaymentIcon from "@mui/icons-material/Payment";
import DiscountIcon from "@mui/icons-material/Discount";
import NotesIcon from "@mui/icons-material/Notes";
import CalculateIcon from "@mui/icons-material/Calculate";
import CloseIcon from "@mui/icons-material/Close";

// Services
import * as saleOrderService from "../services/saleOrderService";
import { generateSaleOrderPdfHtml, generateAndPreviewSaleOrderPDF } from "../services/saleOrderPdfService";

// Components
import CustomerSelect from "../../invoices/components/CustomerSelect";
import ProductSelect from "../../invoices/components/ProductSelect";
import AddCustomerDialog from "../../customers/components/AddCustomerDialog";
import AddProductDialog from "../../product/components/AddProductDialog";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import InvoicePreviewDialog from "../../../components/InvoicePreviewDialog";

// Shared Document Components
import {
  DocumentHeaderSection,
  DocumentDetailsBar,
  DocumentProductsTable,
  DocumentNotesSection,
  DocumentTotalsCard,
} from '../../../components/shared';

// Payment imports
import MultiplePaymentMethodsSection from "../../invoices/components/MultiplePaymentMethodsSection";
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import { useDispatch } from 'react-redux';

// Hooks
import useStoreInfo from '../../../hooks/useCompanyInfo';
import { searchCustomers } from '../../customers/services/customerService';
import { searchProducts } from '../../product/services/productService';

// Constants
const MAX_CUSTOM_TEXT_LENGTH = 1000;

// Generate sale order number
function generateSaleOrderNumber() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const rand = String(Math.floor(1000 + Math.random() * 9000));
  return `SO-${dd}${mm}${yy}${rand}`;
}

function AddSaleOrderPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { storeInfo } = useStoreInfo();
  const language = useSelector((state) => state.settings?.language) || 'en';
  const paymentMethodOptions = useSelector((state) => state.paymentMethod.paymentMethods) || [];
  
  // Get sale order ID from URL params (for reopen/continue)
  const urlParams = new URLSearchParams(window.location.search);
  const saleOrderIdFromUrl = urlParams.get('id');
  
  // Ref to store sale order ID immediately (synchronous)
  const saleOrderIdRef = useRef(null);
  
  // Main state
  const [saleOrderId, setSaleOrderId] = useState(null);
  const [saleOrderNumber, setSaleOrderNumber] = useState("");
  const [saleOrderDate, setSaleOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerChangeInProgress, setCustomerChangeInProgress] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [price, setPrice] = useState("");
  const [customText, setCustomText] = useState("");
  const [saleOrderProducts, setSaleOrderProducts] = useState([]);
  const [vatPercentage, setVatPercentage] = useState(0);
  const [status, setStatus] = useState('Draft'); // 'Draft' or 'Closed'
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  
  // Confirmation dialogs
  const [saveConfirmDialog, setSaveConfirmDialog] = useState(false);
  const [draftConfirmDialog, setDraftConfirmDialog] = useState(false);
  
  // Payment & Discount state
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [useMultiplePayments, setUseMultiplePayments] = useState(false);
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);
  const [invoicePaidAmount, setInvoicePaidAmount] = useState("");
  const [isTvaIncluded, setIsTvaIncluded] = useState(true);

  // Initialize or load existing sale order
  useEffect(() => {
    const loadExistingSaleOrder = async () => {
      if (saleOrderIdFromUrl) {
        try {
          const response = await saleOrderService.getSaleOrderById(saleOrderIdFromUrl);
          const existingSO = response?.payload || response?.data || response;
          
          // Load existing sale order data
          setSaleOrderId(existingSO.Id);
          saleOrderIdRef.current = existingSO.Id; // Store in ref immediately
          setSaleOrderNumber(existingSO.SaleOrderNumber);
          setSaleOrderDate(existingSO.SaleOrderDate?.split('T')[0] || new Date().toISOString().split('T')[0]);
          setStatus(existingSO.Status || 'Draft');
          setVatPercentage(existingSO.VATPercentage || 21);
          setCustomText(existingSO.CustomText || '');
          
          // Load customer
          if (existingSO.CustomerId) {
            const customerData = {
              Id: existingSO.CustomerId,
              CompanyName: existingSO.CustomerName,
              Address: existingSO.Customer.Street+' '+existingSO.Customer.City + ' ' + existingSO.Customer.PostCode,
              VATNumber: existingSO.Customer.VATNumber,
              Name: existingSO.CustomerName,
              Email: existingSO.Customer.Email || '',
            };
            setSelectedCustomer(customerData);
          }
          
          // Load line items
          if (existingSO.LineItems && existingSO.LineItems.length > 0) {
            const loadedItems = existingSO.LineItems.map(item => ({
              ...item,
              _rowKey: Date.now() + Math.random(),
              Name: item.ProductName,
              Price: item.UnitPrice,
            }));
            setSaleOrderProducts(loadedItems);
          }
          
          // Sale order loaded - no toast needed
        } catch (error) {
          console.error('Error loading sale order:', error);
          toast.error(t('failed_to_load_sale_order'));
          setSaleOrderNumber(generateSaleOrderNumber());
        }
      } else {
        setSaleOrderNumber(generateSaleOrderNumber());
      }
    };
    
    loadExistingSaleOrder();
  }, [saleOrderIdFromUrl]);
  
  // Fetch payment methods on mount
  useEffect(() => {
    if (!paymentMethodOptions || paymentMethodOptions.length === 0) {
      dispatch(fetchPaymentMethods());
    }
  }, [dispatch, paymentMethodOptions]);

  // Auto-set price when product is selected
  useEffect(() => {
    if (selectedProduct?.Price) {
      setPrice(String(selectedProduct.Price));
    }
  }, [selectedProduct]);

  // Create sale order when customer is selected (only for new sale orders, not when loading from URL)
  useEffect(() => {
    const createSaleOrderOnCustomerSelect = async () => {
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current;
      
      // Skip if we're loading from URL or if sale order already exists or if creation is in progress
      if (saleOrderIdFromUrl || !selectedCustomer || currentSaleOrderId || customerChangeInProgress) {
        return;
      }
      
      setCustomerChangeInProgress(true);
      try {
        const saleOrderData = {
          SaleOrderNumber: saleOrderNumber,
          SaleOrderDate: saleOrderDate,
          CustomerId: selectedCustomer.Id,
          CustomerName: selectedCustomer.CompanyName || selectedCustomer.Name,
          VATPercentage: vatPercentage,
          CustomText: customText,
          Status: 'Draft',
          PaymentStatus: 'Not Paid',
          PaidAmount: 0,
          TotalIncl: 0,
          LineItems: [],
        };

        const response = await saleOrderService.addSaleOrder(saleOrderData);
        const created = response?.payload || response?.data || response;
        
        // Extract ID from response
        const createdId = created?.Id || created?.id || created;
        
        if (!createdId) {
          console.error('Sale order created but no ID in response:', created);
          toast.error(t('failed_to_create_sale_order'));
          return;
        }
        
        // Set both state and ref immediately
        setSaleOrderId(createdId);
        saleOrderIdRef.current = createdId;
        
        // Update URL with sale order ID for page refresh persistence
        navigate(`/sales/sale-orders/add?id=${createdId}`, { replace: true });
        
        console.log('Sale order created with ID:', createdId);
        // Sale order created - no toast needed
      } catch (error) {
        console.error('Error creating sale order on customer selection:', error);
        toast.error(t('failed_to_create_sale_order'));
      } finally {
        setCustomerChangeInProgress(false);
      }
    };

    createSaleOrderOnCustomerSelect();
  }, [selectedCustomer, saleOrderIdFromUrl]); // Only trigger when customer selection or URL param changes

  // Auto-save: Update sale order when key fields change (with debouncing)
  useEffect(() => {
    const autoSaveTimer = setTimeout(async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      if (currentSaleOrderId && !customerChangeInProgress) {
        try {
          const updateData = {
            CustomText: customText,
            VATPercentage: vatPercentage,
            DiscountType: discountType,
            DiscountValue: discountValue,
          };
          await saleOrderService.updateSaleOrder(currentSaleOrderId, updateData);
          // Silent update - no toast notification for auto-save
        } catch (error) {
          console.error('Auto-save error:', error);
          // Silent failure - don't interrupt user
        }
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(autoSaveTimer);
  }, [customText, vatPercentage, discountType, discountValue, saleOrderId, customerChangeInProgress]);

  // Handle add product - saves directly to DB
  const handleAddProduct = async (productToAdd = null, quantityToAdd = null, priceToAdd = null) => {
    // Check if sale order creation is in progress
    if (customerChangeInProgress) {
      toast.info(t('please_wait_creating_sale_order') || 'Please wait, creating sale order...');
      return;
    }
    
    // Use provided parameters or fall back to state
    const product = productToAdd || selectedProduct;
    const qty = quantityToAdd || quantity;
    const productPrice = priceToAdd || price;

    if (!product) {
      toast.warning(t('please_select_product'));
      return;
    }
    if (qty <= 0) {
      toast.warning(t('please_enter_valid_quantity'));
      return;
    }

    const unitPrice = parseFloat(productPrice) || 0;
    
    // Calculate based on isTvaIncluded
    let totalExcl, vatAmount, totalIncl;
    if (isTvaIncluded) {
      // Price includes VAT - extract VAT from total
      totalIncl = qty * unitPrice;
      totalExcl = totalIncl / (1 + vatPercentage / 100);
      vatAmount = totalIncl - totalExcl;
    } else {
      // Price excludes VAT - add VAT on top
      totalExcl = qty * unitPrice;
      vatAmount = vatPercentage > 0 ? totalExcl * (vatPercentage / 100) : 0;
      totalIncl = totalExcl + vatAmount;
    }

    const lineItemData = {
      ProductId: product.Id,
      ProductName: product.Name,
      Quantity: qty,
      UnitPrice: unitPrice,
      TotalExcl: totalExcl,
      VAT: vatPercentage,
      VATAmount: vatAmount,
      TotalIncl: totalIncl,
    };

    try {
      // Sale order should already exist (created on customer selection or loaded from existing)
      // Use ref as fallback for immediate access after creation
      // Also check URL for ID (in case of page refresh or async timing)
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      if (!currentSaleOrderId) {
        toast.error(t('sale_order_not_created'));
        return;
      }

      // Add line item to existing sale order
      const addedItem = await saleOrderService.addSaleOrderLineItem(currentSaleOrderId, lineItemData);
      const savedItem = addedItem?.payload || addedItem?.data || addedItem;
      
      setSaleOrderProducts([
        ...saleOrderProducts,
        {
          ...savedItem,
          _rowKey: Date.now(),
          Name: product.Name,
          Price: unitPrice,
        }
      ]);

      // Only clear selection if called without parameters (from state-based form)
      if (!productToAdd) {
        setSelectedProduct(null);
        setQuantity(1);
        setPrice("");
      }
      
      // Product added - no toast needed
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error(t('failed_to_add_product'));
    }
  };

  // Handle remove product - deletes from DB
  const handleRemoveProduct = async (product) => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlId = urlParams.get('id');
    const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
    
    if (!currentSaleOrderId) {
      // Just remove from local state if not saved to DB yet
      setSaleOrderProducts(saleOrderProducts.filter((p) => p._rowKey !== product._rowKey));
      return;
    }

    try {
      await saleOrderService.deleteSaleOrderLineItem(currentSaleOrderId, product.Id || product.LineItemId);
      setSaleOrderProducts(saleOrderProducts.filter((p) => p._rowKey !== product._rowKey));
      // Product removed - no toast needed
    } catch (error) {
      console.error('Error removing product:', error);
      toast.error(t('failed_to_remove_product'));
    }
  };

  // Calculate totals with discount based on price type
  const totalExcl = saleOrderProducts.reduce((sum, p) => sum + Number(p.TotalExcl || 0), 0);
  const totalIncl = saleOrderProducts.reduce((sum, p) => sum + Number(p.TotalIncl || 0), 0);
  
  // Calculate discount amount
  let discountAmount = 0;
  let subtotalAfterDiscount = 0;
  let vatAfterDiscount = 0;
  let grandTotal = 0;
  
  if (isTvaIncluded) {
    // Prices already include VAT
    if (discountValue > 0) {
      if (discountType === "percentage") {
        discountAmount = totalIncl * (discountValue / 100);
      } else {
        discountAmount = discountValue;
      }
    }
    subtotalAfterDiscount = totalIncl - discountAmount;
    grandTotal = subtotalAfterDiscount;
    // Calculate VAT portion for display (already included in price)
    vatAfterDiscount = vatPercentage > 0 ? (subtotalAfterDiscount / (1 + vatPercentage / 100)) * (vatPercentage / 100) : 0;
  } else {
    // Prices exclude VAT - calculate VAT on subtotal after discount
    if (discountValue > 0) {
      if (discountType === "percentage") {
        discountAmount = totalExcl * (discountValue / 100);
      } else {
        discountAmount = discountValue;
      }
    }
    subtotalAfterDiscount = totalExcl - discountAmount;
    vatAfterDiscount = vatPercentage > 0 ? subtotalAfterDiscount * (vatPercentage / 100) : 0;
    grandTotal = subtotalAfterDiscount + vatAfterDiscount;
  }
  
  // Handler for full payment
  const handleFullPayment = () => {
    setInvoicePaidAmount(grandTotal.toFixed(2));
  };

  // Handle save as draft
  const handleSaveAsDraft = () => {
    if (!selectedCustomer) {
      toast.warning(t('please_select_customer'));
      return;
    }
    
    if (saleOrderProducts.length === 0) {
      toast.warning(t('please_add_at_least_one_product'));
      return;
    }

    setDraftConfirmDialog(true);
  };

  const confirmSaveAsDraft = async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      if (!currentSaleOrderId) {
        toast.error(t('sale_order_not_created'));
        return;
      }
      
      // Mark as draft (uses /mark-draft endpoint)
      await saleOrderService.markSaleOrderAsDraft(currentSaleOrderId);
      
      toast.success(t('sale_order_saved_as_draft'));
      navigate('/sales/sale-orders');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(t('failed_to_save_draft'));
    } finally {
      setLoading(false);
      setDraftConfirmDialog(false);
    }
  };

  // Handle reopen sale order
  const handleReopenSaleOrder = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlId = urlParams.get('id');
    const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
    
    if (!currentSaleOrderId) {
      toast.error(t('sale_order_not_found'));
      return;
    }
    
    try {
      setLoading(true);
      await saleOrderService.updateSaleOrder(currentSaleOrderId, { Status: 'Draft' });
      setStatus('Draft');
      toast.success(t('sale_order_reopened'));
    } catch (error) {
      console.error('Error reopening sale order:', error);
      toast.error(t('failed_to_reopen_sale_order'));
    } finally {
      setLoading(false);
    }
  };

  // Handle close sale order
  const handleCloseSaleOrder = () => {
    if (!selectedCustomer) {
      toast.warning(t('please_select_customer'));
      return;
    }
    
    if (saleOrderProducts.length === 0) {
      toast.warning(t('please_add_at_least_one_product'));
      return;
    }

    setSaveConfirmDialog(true);
  };

  const confirmCloseSaleOrder = async () => {
    setLoading(true);
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const urlId = urlParams.get('id');
      const currentSaleOrderId = saleOrderId || saleOrderIdRef.current || urlId;
      
      if (!currentSaleOrderId) {
        toast.error(t('sale_order_not_created'));
        return;
      }
      
      // Update and close existing sale order
      const updateData = {
        CustomText: customText,
        VATPercentage: vatPercentage,
      };
      // Update sale order fields before closing
      await saleOrderService.updateSaleOrder(currentSaleOrderId, updateData);
      // Close the sale order (uses /close endpoint)
      await saleOrderService.closeSaleOrder(currentSaleOrderId);
      
      toast.success(t('sale_order_closed_successfully'));
      navigate('/sales/sale-orders');
    } catch (error) {
      console.error('Error closing sale order:', error);
      toast.error(t('failed_to_close_sale_order'));
    } finally {
      setLoading(false);
      setSaveConfirmDialog(false);
    }
  };

  // Handle preview PDF
  const handlePreviewPdf = async () => {
    if (!selectedCustomer || saleOrderProducts.length === 0) {
      toast.warning(t('please_add_customer_and_products'));
      return;
    }

    const saleOrderData = {
      SaleOrderNumber: saleOrderNumber,
      SaleOrderDate: saleOrderDate,
      Customer: selectedCustomer,
      LineItems: saleOrderProducts,
      CustomText: customText,
      VATPercentage: vatPercentage,
      Status: status,
      PaymentStatus: 'Not Paid',
      PaidAmount: invoicePaidAmount || 0,
      TotalIncl: grandTotal,
      DiscountType: discountType,
      DiscountValue: discountValue,
      IsTvaIncluded: isTvaIncluded,
    };

    const htmlContent = generateSaleOrderPdfHtml(saleOrderData, storeInfo, language);
    setPreviewHtml(htmlContent);
    setOpenPreview(true);
  };

  // Handle new customer added
  const handleNewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setOpenCustomerDialog(false);
    toast.success(t('customer_created_and_selected'));
  };

  // Handle new product added
  const handleNewProduct = (product) => {
    setSelectedProduct(product);
    setOpenProductDialog(false);
    toast.success(t('product_created_and_selected'));
  };

  // Load customer options
  const loadCustomerOptions = async (inputValue) => {
    try {
      const data = await searchCustomers(inputValue);
      
      const customersArray = Array.isArray(data?.payload)
        ? data.payload
        : Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : [];

      return customersArray.map((c) => ({
        ...c,
        value: c.Id,
        label: `${c.CompanyName || c.Name} (${c.Email || 'No email'})`,
      }));
    } catch (error) {
      console.error('Error searching customers:', error);
      return [];
    }
  };

  // Load product options with debounce
  const debounceRef = React.useRef();
  const loadProductOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!inputValue) return callback([]);

      try {
        const barcodeLengths = [8, 12, 13, 14];
        let paramsObj;

        if (barcodeLengths.includes(inputValue.length) && /^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (/^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue, id: inputValue };
        } else if (inputValue.length >= 3) {
          paramsObj = { name: inputValue };
        } else {
          return callback([]);
        }

        const data = await searchProducts(paramsObj);

        const productsArray = Array.isArray(data?.payload)
          ? data.payload
          : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        const limitedProducts = productsArray.slice(0, 50);

        callback(
          limitedProducts.map((p) => ({
            value: p.Id,
            label: `${p.Name} (€${p.SalePrice || 0})${p.SKU ? " - SKU: " + p.SKU : ""}${p.BarCode ? " - BarCode: " + p.BarCode : ""}`,
            ...p,
          }))
        );
      } catch (error) {
        console.error('Error searching products:', error);
        callback([]);
      }
    }, 350);
  };

  // Handle quantity change in table
  const handleQuantityChange = (product, newQuantity) => {
    if (newQuantity <= 0) return;
    
    const updatedProducts = saleOrderProducts.map((p) => {
      if (p._rowKey === product._rowKey) {
        const pricePerUnit = parseFloat(p.UnitPrice || p.Price);
        
        let newTotalExcl, newVATAmount, newTotalIncl;
        if (isTvaIncluded) {
          // Price includes VAT
          newTotalIncl = pricePerUnit * newQuantity;
          newTotalExcl = newTotalIncl / (1 + vatPercentage / 100);
          newVATAmount = newTotalIncl - newTotalExcl;
        } else {
          // Price excludes VAT
          newTotalExcl = pricePerUnit * newQuantity;
          newVATAmount = vatPercentage > 0 ? newTotalExcl * (vatPercentage / 100) : 0;
          newTotalIncl = newTotalExcl + newVATAmount;
        }
        
        return {
          ...p,
          Quantity: newQuantity,
          TotalExcl: newTotalExcl.toFixed(2),
          VATAmount: newVATAmount.toFixed(2),
          TotalIncl: newTotalIncl.toFixed(2),
        };
      }
      return p;
    });
    setSaleOrderProducts(updatedProducts);
  };

  // Handle price change in table
  const handlePriceChange = (product, newPrice) => {
    if (newPrice < 0) return;
    
    const updatedProducts = saleOrderProducts.map((p) => {
      if (p._rowKey === product._rowKey) {
        let newTotalExcl, newVATAmount, newTotalIncl;
        if (isTvaIncluded) {
          // Price includes VAT
          newTotalIncl = newPrice * p.Quantity;
          newTotalExcl = newTotalIncl / (1 + vatPercentage / 100);
          newVATAmount = newTotalIncl - newTotalExcl;
        } else {
          // Price excludes VAT
          newTotalExcl = newPrice * p.Quantity;
          newVATAmount = vatPercentage > 0 ? newTotalExcl * (vatPercentage / 100) : 0;
          newTotalIncl = newTotalExcl + newVATAmount;
        }
        
        return {
          ...p,
          Price: newPrice.toFixed(2),
          UnitPrice: newPrice.toFixed(2),
          TotalExcl: newTotalExcl.toFixed(2),
          VATAmount: newVATAmount.toFixed(2),
          TotalIncl: newTotalIncl.toFixed(2),
        };
      }
      return p;
    });
    setSaleOrderProducts(updatedProducts);
  };

  // Handle total incl change in table
  const handleTotalInclChange = (product, newTotalIncl) => {
    const updatedProducts = saleOrderProducts.map((p) => {
      if (p._rowKey === product._rowKey) {
        let newTotalExcl, newVATAmount, newPrice;
        
        if (vatPercentage > 0) {
          const vatRate = vatPercentage / 100;
          newTotalExcl = newTotalIncl / (1 + vatRate);
          newVATAmount = newTotalIncl - newTotalExcl;
        } else {
          // 0% VAT
          newTotalExcl = newTotalIncl;
          newVATAmount = 0;
        }
        
        newPrice = isTvaIncluded 
          ? (newTotalIncl / p.Quantity).toFixed(2) 
          : (newTotalExcl / p.Quantity).toFixed(2);
        
        return {
          ...p,
          TotalIncl: newTotalIncl.toFixed(2),
          TotalExcl: newTotalExcl.toFixed(2),
          VATAmount: newVATAmount.toFixed(2),
          Price: newPrice,
          UnitPrice: newPrice,
        };
      }
      return p;
    });
    setSaleOrderProducts(updatedProducts);
  };

  return (
    <Box sx={{
      background: '#f5f5f5',
      minHeight: '100vh',
      py: 3
    }}>
      <Container 
        maxWidth={false}
        sx={{ 
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          maxWidth: '1600px',
          margin: '0 auto'
        }}
      >
        {/* Sale Order Container - Matches DeliveryNote A4 Style */}
        <Paper
          elevation={3}
          sx={{
            background: 'white',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'visible',
            maxWidth: '100%',
            position: 'relative'
          }}
        >
          {/* Status Ribbon - Top Left Corner */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bgcolor: status === 'Closed' ? '#4caf50' : '#ff9800',
              color: 'white',
              px: 3,
              py: 1,
              fontWeight: 700,
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              borderTopLeftRadius: '8px',
              borderBottomRightRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              zIndex: 10
            }}
          >
            {status === 'Closed' ? t('closed') : t('draft')}
          </Box>
          
          <Box sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 }, pt: { xs: 6, sm: 6, md: 7, lg: 8 } }}>
            {/* Sale Order Number Badge - Always Top Right */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
              <Box sx={{
                p: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '8px'
              }}>
                <Typography variant="overline" fontWeight="600" sx={{ fontSize: '0.75rem', letterSpacing: '1px', display: 'block' }}>
                  {t("sale_order") || "SALE ORDER"}
                </Typography>
                <Typography variant="h6" fontWeight="700" sx={{ letterSpacing: '0.5px' }}>
                  {saleOrderNumber}
                </Typography>
              </Box>
            </Box>

            {/* TOP SECTION: Company Info (Left) + Customer (Right) - Side by Side */}
            <DocumentHeaderSection
              storeInfo={storeInfo}
              selectedCustomer={selectedCustomer}
              onCustomerChange={setSelectedCustomer}
              onCustomerClear={() => setSelectedCustomer(null)}
              onAddCustomerClick={() => setOpenCustomerDialog(true)}
              loadCustomerOptions={loadCustomerOptions}
            />

            {/* SALE ORDER DETAILS ROW: Date, VAT Rate, Items Count */}
            <DocumentDetailsBar
              documentDate={saleOrderDate}
              onDateChange={(date) => setSaleOrderDate(date.toISOString().split('T')[0])}
              vatPercentage={vatPercentage}
              onVatChange={setVatPercentage}
              vatRates={[]}
              itemsCount={saleOrderProducts.length}
              isTvaIncluded={isTvaIncluded}
              onTvaToggle={(checked) => setIsTvaIncluded(checked)}
              dateLabel={t("sale_order_date")}
              disableDateEdit={true}
              showPriceTypeToggle={true}
              showCloseButton={(saleOrderId || saleOrderIdRef.current) && status === 'Draft'}
              onClose={handleCloseSaleOrder}
              showReopenButton={(saleOrderId || saleOrderIdRef.current) && status === 'Closed'}
              onReopen={handleReopenSaleOrder}
              status={status}
            />

            {/* PRODUCTS SECTION: Table with Header Always Visible */}
            <DocumentProductsTable
              products={saleOrderProducts}
              onProductAdd={handleAddProduct}
              onProductRemove={handleRemoveProduct}
              onQuantityChange={handleQuantityChange}
              onPriceChange={handlePriceChange}
              onTotalInclChange={handleTotalInclChange}
              onAddProductClick={() => setOpenProductDialog(true)}
              loadProductOptions={loadProductOptions}
              vatPercentage={vatPercentage}
            />

            {/* BOTTOM SECTION: Payment, Discount, Notes and Totals */}
            <Grid container spacing={3}>
              {/* LEFT: Payment, Discount, Notes */}
              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  {/* Payment Method Section */}
                  <Box sx={{
                    p: 3,
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: '4px solid #667eea',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                      <PaymentIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                      {t("payment_method")}
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={useMultiplePayments}
                          onChange={(e) => {
                            setUseMultiplePayments(e.target.checked);
                            if (e.target.checked) {
                              setPaymentMethod("");
                              setPaymentMethods([]);
                            }
                          }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption" fontWeight={600}>
                          {t("use_multiple_payment_methods")}
                        </Typography>
                      }
                      sx={{ mb: 2 }}
                    />

                    {!useMultiplePayments ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          displayEmpty
                          sx={{ bgcolor: 'white' }}
                        >
                          <MenuItem value="" disabled>
                            <em>{t("select_payment_method") || "Select Payment Method"}</em>
                          </MenuItem>
                          {paymentMethodOptions && paymentMethodOptions.map((pm) => (
                            <MenuItem key={pm.id} value={pm.id}>
                              {pm.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <MultiplePaymentMethodsSection
                        paymentMethods={paymentMethods}
                        onPaymentMethodsChange={setPaymentMethods}
                        paymentMethodOptions={paymentMethodOptions.map(pm => ({
                          value: pm.id,
                          label: pm.name
                        }))}
                        totalAmount={grandTotal}
                      />
                    )}
                  </Box>

                  {/* Discount Section */}
                  <Box sx={{
                    p: 3,
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: '4px solid #ff9800',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                      <DiscountIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                      {t("discount")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={discountType}
                            onChange={(e) => setDiscountType(e.target.value)}
                            sx={{ bgcolor: 'white' }}
                          >
                            <MenuItem value="percentage">{t("percentage")}</MenuItem>
                            <MenuItem value="fixed">{t("fixed_amount")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          type="number"
                          value={discountValue}
                          onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                          size="small"
                          fullWidth
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ bgcolor: 'white' }}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2">{discountType === "percentage" ? "%" : "€"}</Typography>
                                {discountValue > 0 && (
                                  <IconButton
                                    size="small"
                                    onClick={() => setDiscountValue(0)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            )
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Notes Section */}
                  <Box sx={{
                    p: 3,
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    borderLeft: '4px solid #667eea',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                      <NotesIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                      {t("notes")}
                    </Typography>
                    <TextField
                      multiline
                      rows={4}
                      value={customText}
                      onChange={(e) => {
                        const newText = e.target.value;
                        if (newText.length <= MAX_CUSTOM_TEXT_LENGTH) {
                          setCustomText(newText);
                        }
                      }}
                      placeholder={t("add_custom_notes") || "Add custom notes..."}
                      fullWidth
                      sx={{
                        bgcolor: 'white',
                        borderRadius: '4px'
                      }}
                    />
                    <Typography
                      variant="caption"
                      color={customText.length > MAX_CUSTOM_TEXT_LENGTH ? 'error' : 'text.secondary'}
                      sx={{ mt: 1, display: 'block' }}
                    >
                      {customText.length} / {MAX_CUSTOM_TEXT_LENGTH}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* RIGHT: Totals Summary & Action Buttons */}
              <Grid item xs={12} md={5}>
                <Stack spacing={3}>
                  {/* Totals Summary Box */}
                  <Box sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    width: '100%'
                  }}>
                        <Box sx={{ p: 2, background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                          <Typography variant="subtitle1" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', fontSize: '0.9rem' }}>
                            <CalculateIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                            {t("summary")}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                          <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="#555">{t("subtotal")}</Typography>
                              <Typography variant="body1" fontWeight={600}>€{(isTvaIncluded ? totalIncl : totalExcl).toFixed(2)}</Typography>
                            </Box>
                            
                            {/* Discount Display */}
                            {discountValue > 0 && (
                              <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#fff3e0', borderRadius: '4px' }}>
                                  <Typography variant="body2" color="#ff9800" fontWeight={600}>
                                    {t("discount")} {discountType === "percentage" ? `(${discountValue}%)` : ""}
                                  </Typography>
                                  <Typography variant="body1" fontWeight={600} color="#ff9800">
                                    -€{discountAmount.toFixed(2)}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography variant="body2" color="#555" fontWeight={600}>{t("subtotal_after_discount")}</Typography>
                                  <Typography variant="body1" fontWeight={600}>€{subtotalAfterDiscount.toFixed(2)}</Typography>
                                </Box>
                              </>
                            )}
                            
                            {!isTvaIncluded && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#e8f4f8', borderRadius: '4px' }}>
                                <Typography variant="body2" color="#0066cc" fontWeight={600}>{t("vat")} ({vatPercentage}%)</Typography>
                                <Typography variant="body1" fontWeight={600} color="#0066cc">€{vatAfterDiscount.toFixed(2)}</Typography>
                              </Box>
                            )}
                            
                            <Divider sx={{ my: 1 }} />
                            
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 2,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              borderRadius: '6px',
                              mt: 1
                            }}>
                              <Typography variant="h6" fontWeight="700" color="white">{t("total")}</Typography>
                              <Typography variant="h5" fontWeight="700" color="white">€{grandTotal.toFixed(2)}</Typography>
                            </Box>
                            
                            {/* Mark as Fully Paid Button */}
                            <Button
                              variant="outlined"
                              size="small"
                              fullWidth
                              onClick={handleFullPayment}
                              sx={{
                                mt: 2,
                                borderColor: '#667eea',
                                color: '#667eea',
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                  borderColor: '#764ba2',
                                  bgcolor: alpha('#667eea', 0.05)
                                }
                              }}
                            >
                              {t("mark_as_fully_paid")}
                            </Button>
                            
                            {/* Paid Amount Field */}
                            <TextField
                              label={t("paid_amount")}
                              type="number"
                              value={invoicePaidAmount}
                              onChange={(e) => setInvoicePaidAmount(e.target.value)}
                              size="small"
                              fullWidth
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ mt: 2 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">€</InputAdornment>
                              }}
                            />
                          </Stack>
                        </Box>
                      </Box>


                  {/* Action Buttons */}
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={handlePreviewPdf}
                      fullWidth
                      disabled={!selectedCustomer || saleOrderProducts.length === 0}
                      sx={{
                        borderRadius: '8px',
                        py: 1.5,
                        borderColor: '#667eea',
                        color: '#667eea',
                        fontWeight: 600,
                        '&:hover': { borderColor: '#764ba2', bgcolor: alpha('#667eea', 0.05) }
                      }}
                    >
                      {t('preview_pdf')}
                    </Button>

                    {/* Reopen button for closed sale orders */}
                    {status === 'Closed' && (
                      <Button
                        variant="contained"
                        onClick={handleReopenSaleOrder}
                        fullWidth
                        disabled={loading}
                        sx={{ 
                          borderRadius: '8px',
                          py: 1.5,
                          bgcolor: '#2196f3', 
                          fontWeight: 600,
                          '&:hover': { bgcolor: '#1976d2' } 
                        }}
                      >
                        {t('reopen_sale_order')}
                      </Button>
                    )}

                    {/* Draft/Close buttons only for non-closed orders */}
                    {status !== 'Closed' && (
                      <>
                        <Button
                          variant="contained"
                          startIcon={<DraftsIcon />}
                          onClick={handleSaveAsDraft}
                          fullWidth
                          disabled={loading || !selectedCustomer || saleOrderProducts.length === 0}
                          sx={{ 
                            borderRadius: '8px',
                            py: 1.5,
                            bgcolor: '#ff9800', 
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#f57c00' } 
                          }}
                        >
                          {t('save_as_draft')}
                        </Button>

                        <Button
                          variant="contained"
                          startIcon={<CheckCircleIcon />}
                          onClick={handleCloseSaleOrder}
                          fullWidth
                          disabled={loading || !selectedCustomer || saleOrderProducts.length === 0}
                          sx={{ 
                            borderRadius: '8px',
                            py: 1.5,
                            bgcolor: '#4caf50', 
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#388e3c' } 
                          }}
                        >
                          {t('close_sale_order')}
                        </Button>
                      </>
                    )}

                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate('/sales/sale-orders')}
                      fullWidth
                      sx={{
                        borderRadius: '8px',
                        py: 1.5,
                        fontWeight: 600,
                      }}
                    >
                      {t('back')}
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      {/* Dialogs */}
      <AddCustomerDialog
        open={openCustomerDialog}
        onClose={() => setOpenCustomerDialog(false)}
        onCustomerAdded={handleNewCustomer}
      />

      <AddProductDialog
        open={openProductDialog}
        onClose={() => setOpenProductDialog(false)}
        onProductAdded={handleNewProduct}
      />

      <InvoicePreviewDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        html={previewHtml}
        documentTitle={`${t('sale_order')} ${saleOrderNumber}`}
      />

      <ConfirmationDialog
        open={draftConfirmDialog}
        title={t('save_as_draft')}
        message={t('confirm_save_draft_message')}
        onConfirm={confirmSaveAsDraft}
        onCancel={() => setDraftConfirmDialog(false)}
        confirmText={t('save')}
        cancelText={t('cancel')}
        variant="warning"
        loading={loading}
      />

      <ConfirmationDialog
        open={saveConfirmDialog}
        title={t('close_sale_order')}
        message={
          <Box>
            <Typography variant="body1" fontWeight={600} gutterBottom>
              {t('confirm_close_sale_order_summary')}
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('sale_order_number')}:</Typography>
                  <Typography variant="body2" fontWeight={600}>{saleOrderNumber}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('number_of_items')}:</Typography>
                  <Typography variant="body2" fontWeight={600}>{saleOrderProducts.length}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('total_amount')}:</Typography>
                  <Typography variant="body2" fontWeight={600} color="primary">€{grandTotal.toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">{t('paid_amount')}:</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">€{(invoicePaidAmount || 0)}</Typography>
                </Grid>
                {(grandTotal - (invoicePaidAmount || 0)) > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">{t('due_amount')}:</Typography>
                    <Typography variant="body2" fontWeight={600} color="error.main">€{(grandTotal - (invoicePaidAmount || 0)).toFixed(2)}</Typography>
                  </Grid>
                )}
                {(useMultiplePayments ? paymentMethods.length > 0 : paymentMethod) && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">{t('payment_method')}:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {useMultiplePayments 
                        ? paymentMethods.map(pm => {
                            const pmOption = paymentMethodOptions.find(opt => opt.id === pm.paymentMethodId);
                            return pmOption ? `${pmOption.name} (€${pm.amount})` : '';
                          }).join(', ')
                        : paymentMethodOptions.find(pm => pm.id === paymentMethod)?.name || '-'
                      }
                    </Typography>
                  </Grid>
                )}
                {discountValue > 0 && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">{t('discount')}:</Typography>
                    <Typography variant="body2" fontWeight={600} color="warning.main">
                      {discountType === 'percentage' ? `${discountValue}%` : `€${discountValue}`}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('are_you_sure_close_sale_order')}
            </Typography>
          </Box>
        }
        onConfirm={confirmCloseSaleOrder}
        onCancel={() => setSaveConfirmDialog(false)}
        confirmText={t('confirm_close')}
        cancelText={t('edit')}
        variant="success"
        loading={loading}
      />
    </Box>
  );
}

export default AddSaleOrderPage;
